/**
 * Phase-1 hardcoded test map: workhall (with a 4×3 grid of cubicles) —
 * corridor — armory. See CLAUDE.md CURRENT PHASE for the intent.
 *
 * Layout constants at the top so it's easy to tweak; buildTestMap is
 * mechanical.
 */

import { CONFIG } from '../config';
import {
  autotileCubicles,
  blankMap,
  fillRect,
  outlineRect,
  refreshDoorGlyph,
  setTile,
  tileAt,
  type MapData,
  type Room,
} from './map';
import { computeLightField } from './light';
import type { PatrolRoute } from './guard';
import { createContainer } from './container';
import { createMop } from './inventory';
import { createLock } from './lock';
import { TileType } from './tiles';

/** Place a CLOSED, normal, unlocked door at (x, y) — a real toggleable door that
 *  anyone opens with E and NPCs auto-open to pass through. Doors ALWAYS spawn
 *  closed (they read as a framed `║` doorway that blocks sight/sound until
 *  opened). Deduped so overlapping wall punches don't double-register. */
function addClosedDoor(map: MapData, x: number, y: number): void {
  setTile(map, x, y, TileType.DoorClosed);
  if (!map.doors.some((d) => d.x === x && d.y === y)) {
    map.doors.push({ x, y, kind: 'normal', lockId: null });
  }
}

/** Overall map extents. Extended south for the safe spawn room. */
const WIDTH = 84;
const HEIGHT = 58;

/** Workhall footprint. */
const WH = { x0: 0, y0: 0, x1: 61, y1: 39 };

/** Safe spawn room — south of the workhall, connected via a doorway.
 *  No default patrols reach here so the player can walk around freely. */
const SPAWN_ROOM = { x0: 24, y0: 44, x1: 39, y1: 55 };
/** Doorway from spawn room north into the workhall (2 tiles wide). */
const SPAWN_DOOR = { x0: 30, x1: 31, y0: 40, y1: 43 };

/** 4×3 grid of cubicles inside the workhall. */
const CUB = {
  cols: 4,
  rows: 3,
  w: 9,
  h: 5,
  /** Top-left of the top-left cubicle. */
  originX: 6,
  originY: 8,
  /** Gap between adjacent cubicles (both axes). */
  gap: 4,
};

/** Corridor punching east through the workhall wall into the armory. */
const CORRIDOR = { x0: 61, x1: 68, y0: 20, y1: 21 };

/** Armory rectangle. */
const ARM = { x0: 68, x1: 82, y0: 16, y1: 26 };

/** Player spawn: middle of the safe spawn room. */
const SPAWN = { x: 31, y: 50 };

/** Janitor's supply closet — a 5×5 room bolted onto the spawn room's east wall,
 *  sharing a doorway with it. Not patrolled by guards. */
const JAN_CLOSET = { x0: 39, y0: 46, x1: 43, y1: 50 };
/** Where the janitor idles (closet interior, by the door). */
const JAN_SPAWN = { x: 41, y: 48 };

/** The janitor's idle/spawn tile (closet). */
export function getJanitorSpawn(): { x: number; y: number } {
  return { ...JAN_SPAWN };
}

/** Green room — where surplus guards retire to rest — directly south of the
 *  armory (disjoint from it), joined by a doorway through both walls. Not
 *  patrolled. */
const GREEN = { x0: 70, y0: 27, x1: 80, y1: 34 };
/** Green room centre (rest point). */
const GREEN_CENTER = { x: 75, y: 31 };

/** The green room's rest-point centre tile. */
export function getGreenRoom(): { x: number; y: number } {
  return { ...GREEN_CENTER };
}

/** Cubicle carve: outer partition rectangle with a one-tile opening on `openSide`. */
function carveCubicle(
  map: MapData,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  openSide: 'N' | 'S' | 'E' | 'W',
): void {
  outlineRect(map, x0, y0, x1, y1, TileType.CubiclePartition);
  fillRect(map, x0 + 1, y0 + 1, x1 - 1, y1 - 1, TileType.Floor);
  const mx = Math.floor((x0 + x1) / 2);
  const my = Math.floor((y0 + y1) / 2);
  if (openSide === 'N') setTile(map, mx, y0, TileType.Floor);
  else if (openSide === 'S') setTile(map, mx, y1, TileType.Floor);
  else if (openSide === 'E') setTile(map, x1, my, TileType.Floor);
  else setTile(map, x0, my, TileType.Floor);
}

export function buildTestMap(): MapData {
  const map = blankMap(WIDTH, HEIGHT);
  map.title = 'Chagat Corp';
  map.floor = 1;

  // Workhall
  fillRect(map, WH.x0, WH.y0, WH.x1, WH.y1, TileType.Floor);
  outlineRect(map, WH.x0, WH.y0, WH.x1, WH.y1, TileType.Wall);

  // 4×3 cubicle grid. Rows 0 and 1 open south, row 2 opens north — creates
  // two horizontal aisles running east–west across the workhall.
  for (let row = 0; row < CUB.rows; row++) {
    for (let col = 0; col < CUB.cols; col++) {
      const x0 = CUB.originX + col * (CUB.w + CUB.gap);
      const y0 = CUB.originY + row * (CUB.h + CUB.gap);
      const x1 = x0 + CUB.w - 1;
      const y1 = y0 + CUB.h - 1;
      const side = row < CUB.rows - 1 ? 'S' : 'N';
      carveCubicle(map, x0, y0, x1, y1, side);
    }
  }

  // Corridor: 2-tile-wide passage through the workhall east wall into the armory.
  fillRect(map, CORRIDOR.x0, CORRIDOR.y0, CORRIDOR.x1, CORRIDOR.y1, TileType.Floor);
  // Door endpoints — a double door across each end of the 2-wide passage.
  addClosedDoor(map, CORRIDOR.x0, CORRIDOR.y0);
  addClosedDoor(map, CORRIDOR.x0, CORRIDOR.y1);
  addClosedDoor(map, CORRIDOR.x1, CORRIDOR.y0);
  addClosedDoor(map, CORRIDOR.x1, CORRIDOR.y1);
  // Corridor walls flanking the passage.
  for (let x = CORRIDOR.x0 + 1; x < CORRIDOR.x1; x++) {
    setTile(map, x, CORRIDOR.y0 - 1, TileType.Wall);
    setTile(map, x, CORRIDOR.y1 + 1, TileType.Wall);
  }

  // Armory
  fillRect(map, ARM.x0, ARM.y0, ARM.x1, ARM.y1, TileType.Floor);
  outlineRect(map, ARM.x0, ARM.y0, ARM.x1, ARM.y1, TileType.Wall);
  // Re-place corridor doors (outlineRect above overwrote them with walls).
  addClosedDoor(map, CORRIDOR.x1, CORRIDOR.y0);
  addClosedDoor(map, CORRIDOR.x1, CORRIDOR.y1);
  // Pickups against the far wall of the armory.
  const armMy = Math.floor((ARM.y0 + ARM.y1) / 2);
  setTile(map, ARM.x1 - 2, armMy - 1, TileType.AmmoPickup);
  setTile(map, ARM.x1 - 2, armMy + 1, TileType.HealStation);

  // Safe spawn room south of the workhall. Guards don't patrol here.
  fillRect(map, SPAWN_ROOM.x0, SPAWN_ROOM.y0, SPAWN_ROOM.x1, SPAWN_ROOM.y1, TileType.Floor);
  outlineRect(map, SPAWN_ROOM.x0, SPAWN_ROOM.y0, SPAWN_ROOM.x1, SPAWN_ROOM.y1, TileType.Wall);
  // Doorway punch: carve the corridor between the workhall's south wall
  // and the spawn room's north wall.
  fillRect(map, SPAWN_DOOR.x0, SPAWN_DOOR.y0, SPAWN_DOOR.x1, SPAWN_DOOR.y1, TileType.Floor);
  // Double doors on both faces of the vestibule between workhall and spawn room.
  addClosedDoor(map, SPAWN_DOOR.x0, WH.y1);
  addClosedDoor(map, SPAWN_DOOR.x1, WH.y1);
  addClosedDoor(map, SPAWN_DOOR.x0, SPAWN_ROOM.y0);
  addClosedDoor(map, SPAWN_DOOR.x1, SPAWN_ROOM.y0);

  // Janitor's supply closet on the spawn room's east wall.
  fillRect(map, JAN_CLOSET.x0, JAN_CLOSET.y0, JAN_CLOSET.x1, JAN_CLOSET.y1, TileType.Floor);
  outlineRect(map, JAN_CLOSET.x0, JAN_CLOSET.y0, JAN_CLOSET.x1, JAN_CLOSET.y1, TileType.Wall);
  // Janitor-ROOM entrance door — a normal, UNLOCKED door (anyone opens with E).
  // Starts CLOSED so it blocks sight/sound until opened.
  setTile(map, JAN_CLOSET.x0, 48, TileType.DoorClosed);
  map.doors.push({ x: JAN_CLOSET.x0, y: 48, kind: 'normal', lockId: null });
  // Closet cabinet — a LOCKED lootable container (holds the mop). Its tile stays
  // a blocking prop wall with the '⌂' glyph; the loot + lock are map data.
  const cabX = JAN_CLOSET.x1 - 1; // 42
  const cabY = JAN_CLOSET.y0 + 1; // 47
  setTile(map, cabX, cabY, TileType.Wall);
  map.glyphs[cabY * map.width + cabX] = '⌂';
  const cabinet = createContainer({ x: cabX, y: cabY, glyph: '⌂', items: [createMop()] });
  const cabinetLock = createLock({ owner: 'container', ownerId: cabinet.id, x: cabX, y: cabY, securityLevel: 2 });
  cabinet.lockId = cabinetLock.id;
  map.locks.push(cabinetLock);
  map.containers.push(cabinet);
  map.glyphs[cabY * map.width + (JAN_CLOSET.x0 + 2)] = 'U'; // bucket (decorative)

  // Green room (rest area) south of the armory. A doorway punches through both
  // the armory's south wall (y=ARM.y1) and the green room's north wall.
  fillRect(map, GREEN.x0, GREEN.y0, GREEN.x1, GREEN.y1, TileType.Floor);
  outlineRect(map, GREEN.x0, GREEN.y0, GREEN.x1, GREEN.y1, TileType.Wall);
  addClosedDoor(map, GREEN_CENTER.x, ARM.y1); // through the armory south wall
  addClosedDoor(map, GREEN_CENTER.x, GREEN.y0); // through the green-room north wall

  autotileCubicles(map);

  // -- Rooms + lighting -----------------------------------------------------
  // Named rooms whose breakers the fusebox switches. Bounds are the room
  // rectangles (walls inclusive); they're disjoint so tile→room is unambiguous.
  map.rooms = [
    { id: 1, name: 'Workhall', x0: WH.x0, y0: WH.y0, x1: WH.x1, y1: WH.y1, lightsOn: true },
    { id: 2, name: 'Corridor', x0: CORRIDOR.x0, y0: CORRIDOR.y0 - 1, x1: CORRIDOR.x1, y1: CORRIDOR.y1 + 1, lightsOn: true },
    { id: 3, name: 'Armory', x0: ARM.x0, y0: ARM.y0, x1: ARM.x1, y1: ARM.y1, lightsOn: true },
    { id: 4, name: 'Spawn Room', x0: SPAWN_ROOM.x0, y0: SPAWN_ROOM.y0, x1: SPAWN_ROOM.x1, y1: SPAWN_ROOM.y1, lightsOn: true },
    { id: 5, name: 'Janitor', x0: JAN_CLOSET.x0, y0: JAN_CLOSET.y0, x1: JAN_CLOSET.x1, y1: JAN_CLOSET.y1, lightsOn: true, noPatrol: true },
    { id: 6, name: 'Green Room', x0: GREEN.x0, y0: GREEN.y0, x1: GREEN.x1, y1: GREEN.y1, lightsOn: true, noPatrol: true },
  ];
  // Ceiling fixtures on a grid across each room so the whole floor is covered.
  scatterCeilingLights(map, map.rooms[0]!, 11); // large workhall
  scatterCeilingLights(map, map.rooms[1]!, 6); // corridor
  scatterCeilingLights(map, map.rooms[2]!, 8); // armory
  scatterCeilingLights(map, map.rooms[3]!, 8); // spawn room
  scatterCeilingLights(map, map.rooms[4]!, 6); // janitor closet
  scatterCeilingLights(map, map.rooms[5]!, 6); // green room

  // Fusebox in the spawn room (the level's entry point, by the door north into
  // the workhall). Player stands on the floor just south of it and presses E.
  setTile(map, 27, SPAWN_ROOM.y0, TileType.Fusebox);
  map.fusebox = { x: 27, y: SPAWN_ROOM.y0 };

  // Windows in the exterior end walls admit sunlight — NATURAL light sources,
  // off the electrical grid (the fusebox/blackout can never switch them). main.ts
  // sets their intensity from the operation's time of day before the field is
  // (re)computed.
  const addWindow = (x: number, y: number): void => {
    setTile(map, x, y, TileType.Window);
    map.lights.push({
      x,
      y,
      roomId: 0,
      intensity: CONFIG.light.windowIntensity,
      radius: CONFIG.light.windowRadius,
      mount: 'wall',
      natural: true,
    });
  };
  // Workhall north wall (the top end of the floor).
  addWindow(18, WH.y0);
  addWindow(31, WH.y0);
  addWindow(44, WH.y0);
  // Armory far (east) wall.
  const armMid = Math.floor((ARM.y0 + ARM.y1) / 2);
  addWindow(ARM.x1, armMid - 2);
  addWindow(ARM.x1, armMid + 2);

  computeLightField(map);

  map.spawn = { x: SPAWN.x, y: SPAWN.y };
  // Doors take the glyph of the wall they sit in (open and closed never share
  // one) — stamped last, when every wall around them exists.
  for (const d of map.doors) refreshDoorGlyph(map, d.x, d.y);

  return map;
}

/**
 * Place ceiling light fixtures on a `spacing`-tile grid across a room, nudging
 * each onto the nearest floor tile so a grid point landing on a wall/partition
 * still lights its neighbourhood. Union of the fixtures' radii covers the room.
 */
function scatterCeilingLights(map: MapData, room: Room, spacing: number): void {
  const half = Math.floor(spacing / 2);
  for (let y = room.y0 + half; y <= room.y1 - 1; y += spacing) {
    for (let x = room.x0 + half; x <= room.x1 - 1; x += spacing) {
      const spot = nearestFloor(map, x, y, 3);
      if (!spot) continue;
      map.lights.push({
        x: spot.x,
        y: spot.y,
        roomId: room.id,
        intensity: CONFIG.light.fixtureIntensity,
        radius: CONFIG.light.fixtureRadius,
        mount: 'ceiling',
      });
    }
  }
}

/** Nearest floor tile to (x, y) within Chebyshev radius `r`, or null. */
function nearestFloor(map: MapData, x: number, y: number, r: number): { x: number; y: number } | null {
  for (let rad = 0; rad <= r; rad++) {
    for (let dy = -rad; dy <= rad; dy++) {
      for (let dx = -rad; dx <= rad; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== rad) continue;
        if (tileAt(map, x + dx, y + dy) === TileType.Floor) return { x: x + dx, y: y + dy };
      }
    }
  }
  return null;
}

/**
 * Default guard patrol loops for the test map. Coordinates are picked so
 * the routes trace the walkable aisles around the cubicle grid + the
 * corridor + the armory. A* handles the actual step-by-step routing;
 * these are just the waypoint loops.
 */
export function getDefaultPatrols(): PatrolRoute[] {
  return [
    // Workhall perimeter (clockwise from NW corner of the interior).
    [
      { x: 3, y: 3 },
      { x: 58, y: 3 },
      { x: 58, y: 37 },
      { x: 3, y: 37 },
    ],
    // Middle horizontal aisle sweep (A* routes around cubicle half-walls).
    // Starts east of the player spawn so guards don't spawn on top of them.
    [
      { x: 18, y: 15 },
      { x: 58, y: 15 },
    ],
    // Corridor sentry between workhall and armory.
    [
      { x: 62, y: 20 },
      { x: 67, y: 20 },
    ],
    // Armory patroller.
    [
      { x: 70, y: 20 },
      { x: 80, y: 20 },
      { x: 70, y: 20 },
    ],
  ];
}
